/**
 * Textos das tools WebMCP expostas aos agentes de IA.
 *
 * O `name` de cada tool é identificador estável (snake_case em inglês) e NÃO
 * vive aqui — só as descrições, as descrições de parâmetro e as strings que o
 * `execute` devolve, que trocam conforme /pt-br ou /en (o `useWebMcpTools`
 * re-registra as tools quando `route.params.language` muda).
 *
 * Convenção das descrições (best practices do Chrome): frase afirmativa
 * dizendo o que a tool FAZ, sem listar limitações; e nome que separa execução
 * imediata de início de fluxo — daí o prefixo `start_` nas que só levam o
 * usuário até o formulário.
 */

interface ToolText {
  description: string;
  params: Record<string, string>;
}

interface WebMcpTranslation {
  tools: {
    search_models: ToolText;
    browse_catalog: ToolText;
    list_categories: ToolText;
    open_page: ToolText;
    set_language: ToolText;
    get_subscription_plans: ToolText;
    start_subscription: ToolText;
    get_account_status: ToolText;
    get_credit_packages: ToolText;
    get_my_credits: ToolText;
    get_purchase_history: ToolText;
    start_account_update: ToolText;
    start_plan_renewal: ToolText;
    start_referral_code: ToolText;
    start_subscription_cancellation: ToolText;
    open_help_topic: ToolText;
    contact_support: ToolText;
    suggest_model: ToolText;
    request_password_reset: ToolText;
    read_article: ToolText;
    get_model_details: ToolText;
    get_whats_new: ToolText;
    get_help_answer: ToolText;
    get_download_status: ToolText;
    control_video: ToolText;
    choose_video: ToolText;
    navigate_photos: ToolText;
    photo_slideshow: ToolText;
    /**
     * AINDA NÃO LIGADA: `ModalNewsletter.vue` não tem elemento `<form>` (são
     * inputs soltos com um botão), e a API declarativa precisa de um. Envolver
     * o modal num form mudaria como ele submete hoje. O texto fica pronto para
     * quando esse refactor acontecer.
     */
    subscribe_newsletter: ToolText;
  };
  messages: {
    notLoggedIn: string;
    noResults: string;
    error: string;
    navigated: string;
    languageChanged: string;
    unknownSection: string;
    unknownDestination: string;
    modelNotFound: string;
    confirmInPage: string;
    topicNotFound: string;
    topicOpened: string;
    planSelectionOpen: string;
    searchOnlyArticles: string;
    articleNotFound: string;
    notAnArticle: string;
    textTruncated: string;
    autoplayBlocked: string;
    noPlayer: string;
    videoPartNotFound: string;
    videoPartWithoutVideo: string;
    videoQualityNotFound: string;
    videoQuality4k: string;
    liveWithoutQuality: string;
    videoPreviewOnly: string;
    makingOfPreviewOnly: string;
    livePreviewOnly: string;
    photoPartNotFound: string;
    photoLocked: string;
    photoEnd: string;
    photoStart: string;
  };
}

const webmcp: { portugues: WebMcpTranslation; ingles: WebMcpTranslation } = {
  portugues: {
    tools: {
      search_models: {
        description:
          "Busca modelos do Bella da Semana pelo nome e devolve o nome e o endereço da página de cada uma. Use quando a pessoa mencionar o nome de uma modelo.",
        params: {
          query: "Nome ou parte do nome da modelo, por exemplo 'ana paula'.",
          limit: "Quantos resultados devolver (1 a 10). O padrão é 8.",
        },
      },
      browse_catalog: {
        description:
          "Lista o conteúdo publicado no Bella da Semana por seção (ensaios de modelos, vídeos, making of, lives, colunas ou blog), com paginação, devolvendo títulos e endereços de página. No blog e nas colunas, também busca por palavra.",
        params: {
          section:
            "Seção do catálogo: 'models' (ensaios fotográficos), 'videos', 'making-of', 'lives', 'columns' (colunas) ou 'blog'.",
          category:
            "Slug da categoria para filtrar, obtido em list_categories. Vale para models, videos, making-of, lives e columns.",
          query:
            "Palavra ou trecho para buscar, por exemplo 'praia'. Vale para blog e columns; para achar uma modelo, use search_models.",
          page: "Número da página, começando em 1.",
        },
      },
      list_categories: {
        description:
          "Lista as categorias disponíveis em uma seção do catálogo, com o nome e o slug de cada uma (e quantos itens tem, quando a seção informa). Use antes de filtrar em browse_catalog.",
        params: {
          section:
            "Seção do catálogo: 'models', 'videos', 'making-of', 'lives' ou 'columns' (colunas).",
        },
      },
      open_page: {
        description:
          "Abre uma página do Bella da Semana no navegador, incluindo as páginas de uma modelo específica (ensaio, vídeos, making of, lives, perfil e wallpapers).",
        params: {
          destination:
            "Para onde ir: home, models, videos, making-of, lives, columns, blog, ecards, freetour, bellacloud, about, contact, press, subscribe, login, my-account, my-credits, palpitao, area-gold ou model (a página de uma modelo).",
          model_slug:
            "Slug da modelo, obrigatório quando destination é 'model'. Use search_models para descobrir o slug.",
          model_section:
            "Qual página da modelo abrir: photo (ensaio), video, making-of, live, perfil ou wallpaper. O padrão é photo.",
        },
      },
      set_language: {
        description:
          "Troca o idioma do site entre português e inglês, mantendo a pessoa na mesma página.",
        params: { language: "Idioma desejado: 'pt-br' ou 'en'." },
      },
      get_subscription_plans: {
        description:
          "Devolve os planos de assinatura do Bella da Semana em vigor agora, com tipo (Basic, Plus, Gold), duração em meses, preço, desconto e o que cada um libera.",
        params: {},
      },
      start_subscription: {
        description:
          "Leva a pessoa para o início da contratação de uma assinatura, já com o plano escolhido e o cupom aplicado. O pagamento continua sendo preenchido e confirmado pela própria pessoa.",
        params: {
          plan_type: "Tipo do plano: 'Basic', 'Plus' ou 'Gold'.",
          months: "Duração do plano em meses, por exemplo 1, 3 ou 12.",
          coupon: "Cupom de desconto a aplicar, se houver.",
        },
      },
      get_account_status: {
        description:
          "Informa se a pessoa está com sessão iniciada no Bella da Semana, se tem assinatura ativa, qual o plano e até quando vale.",
        params: {},
      },
      get_credit_packages: {
        description:
          "Devolve os pacotes de créditos de download à venda, com quantidade de créditos e preço.",
        params: {},
      },
      get_my_credits: {
        description:
          "Informa quantos créditos de download a pessoa tem disponíveis e quantos downloads já usou no dia.",
        params: {},
      },
      get_purchase_history: {
        description:
          "Devolve o histórico de compras da conta com data, plano, valor e moeda de cada pedido.",
        params: {},
      },
      start_account_update: {
        description:
          "Abre a aba de dados cadastrais da Minha Conta para a pessoa revisar e salvar as alterações.",
        params: {},
      },
      start_plan_renewal: {
        description:
          "Abre a aba de renovação e upgrade de plano da Minha Conta, onde a pessoa escolhe e confirma o novo plano.",
        params: {},
      },
      start_referral_code: {
        description:
          "Abre a aba 'Convide seu amigo' da Minha Conta, onde a pessoa gera o código de compartilhamento.",
        params: {},
      },
      start_subscription_cancellation: {
        description:
          "Abre o formulário de cancelamento de assinatura. O cancelamento em si só acontece quando a própria pessoa preenche o motivo e confirma.",
        params: {},
      },
      open_help_topic: {
        description:
          "Abre um tópico do FAQ da página de contato. Abrir o tópico é o que traz à tona os formulários de contato, sugestão de modelo e redefinição de senha.",
        params: {
          topic:
            "Título do tópico do FAQ a abrir, exatamente como aparece na lista.",
        },
      },
      contact_support: {
        description:
          "Envia uma mensagem para o atendimento do Bella da Semana. Preenche o formulário de contato para a pessoa revisar e enviar.",
        params: {},
      },
      suggest_model: {
        description:
          "Sugere uma modelo para o Bella da Semana. Preenche o formulário de sugestão para a pessoa revisar e enviar.",
        params: {},
      },
      request_password_reset: {
        description:
          "Pede o e-mail de redefinição de senha. Preenche o formulário para a pessoa revisar e enviar.",
        params: {},
      },
      read_article: {
        description:
          "Lê um post do blog ou uma coluna do Bella da Semana e devolve o título, a data e o texto, sem imagens. Use para resumir um artigo ou responder perguntas sobre ele.",
        params: {
          url: "Endereço do post ou da coluna, como devolvido por browse_catalog ou get_whats_new. Sem endereço, lê o artigo aberto na página atual.",
        },
      },
      get_model_details: {
        description:
          "Devolve os detalhes de uma modelo: apresentação, data de publicação do ensaio, entrevista, créditos da produção e o que ela tem no site (fotos, vídeos, making of, lives), com o endereço de cada página.",
        params: {
          model_slug: "Slug da modelo. Use search_models para descobrir.",
        },
      },
      get_whats_new: {
        description:
          "Mostra as novidades do Bella da Semana: a modelo da semana e os ensaios, making of, lives, posts do blog e colunas mais recentes, com o endereço de cada um.",
        params: {},
      },
      get_help_answer: {
        description:
          "Responde a uma dúvida frequente com o texto da Central de Ajuda (renovação, upgrade, bônus, acesso a fotos e vídeos, status da conta, créditos, senha, cache, sugestões, cancelamento ou contato) e devolve o endereço da página que resolve.",
        params: {
          topic: "Título do tópico da Central de Ajuda, exatamente como na lista.",
        },
      },
      get_download_status: {
        description:
          "Mostra os vídeos que a pessoa pediu para baixar com créditos: se cada um está disponível, expirado ou ainda processando, e até quando o link vale. O download em si é feito pela pessoa na página de downloads.",
        params: {},
      },
      control_video: {
        description:
          "Controla o player de vídeo aberto nesta página: dar play, pausar ou recomeçar do início. Devolve se está tocando, a parte, a qualidade e o tempo.",
        params: {
          action: "'play', 'pause' ou 'restart' (volta ao início e dá play).",
        },
      },
      choose_video: {
        description:
          "Escolhe a parte e a qualidade do vídeo aberto nesta página (vídeo do ensaio, making of ou live; nas lives, só a parte). Sem argumentos, só lista as partes e as qualidades disponíveis.",
        params: {
          part: "Número da parte, começando em 1.",
          quality: "Qualidade do vídeo, exatamente como aparece na lista, por exemplo 'HD 720p'.",
        },
      },
      navigate_photos: {
        description:
          "Passa as fotos do ensaio aberto nesta página: próxima, anterior ou o começo de uma parte. Devolve a parte, a seção e a posição da foto.",
        params: {
          action: "'next' (próxima), 'previous' (anterior) ou 'part' (ir para uma parte).",
          part: "Número da parte, começando em 1. Obrigatório quando action é 'part'.",
        },
      },
      photo_slideshow: {
        description:
          "Liga ou desliga a passagem automática das fotos do ensaio aberto nesta página, com o intervalo escolhido.",
        params: {
          action: "'start' (ligar) ou 'stop' (desligar).",
          seconds: "Segundos entre uma foto e outra, de 1 a 10. Sem valor, mantém o intervalo atual.",
        },
      },
      subscribe_newsletter: {
        description:
          "Cadastra um e-mail na newsletter do Bella da Semana. Preenche o formulário para a pessoa revisar e enviar.",
        params: {},
      },
    },
    messages: {
      notLoggedIn:
        "Ninguém está com sessão iniciada neste navegador. Peça para a pessoa entrar na conta em /pt-br/login e tente de novo.",
      noResults: "Nenhum resultado encontrado.",
      error: "Não foi possível concluir agora. Tente novamente em instantes.",
      navigated: "Página aberta:",
      languageChanged: "Idioma alterado para",
      unknownSection: "Seção desconhecida.",
      unknownDestination: "Destino desconhecido.",
      modelNotFound: "Modelo não encontrada. Use search_models para achar o slug correto.",
      confirmInPage:
        "Nada foi enviado: a pessoa precisa conferir e confirmar na própria página.",
      topicNotFound: "Tópico não encontrado na lista do FAQ.",
      topicOpened: "Tópico aberto:",
      planSelectionOpen:
        "A tela de planos está aberta com o tipo escolhido. A pessoa escolhe o período e confirma o pagamento — nada foi cobrado.",
      searchOnlyArticles:
        "A busca por palavra vale só para blog e columns. Para achar uma modelo, use search_models.",
      articleNotFound:
        "Post ou coluna não encontrado. Use browse_catalog para achar o endereço certo.",
      notAnArticle:
        "Esse endereço não é de um post do blog nem de uma coluna. Passe o endereço de um artigo, como devolvido por browse_catalog.",
      textTruncated: "O texto foi cortado aqui; o restante está na página do artigo.",
      autoplayBlocked:
        "O navegador não deixou o vídeo começar sozinho. Peça para a pessoa clicar no play do player.",
      noPlayer: "O player ainda está carregando. Tente de novo em instantes.",
      videoPartNotFound: "Parte inexistente neste vídeo. Use choose_video sem argumentos para ver as partes.",
      videoPartWithoutVideo: "Essa parte não tem vídeo.",
      videoQualityNotFound:
        "Qualidade indisponível nesta parte. Use choose_video sem argumentos para ver as qualidades.",
      videoQuality4k:
        "Qualidade 4K exclusiva para membros com planos Plus e Gold. Para assistir em 4K, faça o upgrade do seu plano ou utilize créditos para download.",
      liveWithoutQuality: "As lives não têm escolha de qualidade.",
      videoPreviewOnly:
        "Quem não é assinante vê só a prévia de cada parte; a escolha de qualidade e o vídeo completo são exclusivos para assinantes.",
      makingOfPreviewOnly:
        "Quem não é assinante Plus ou Gold vê só a prévia de cada parte; a escolha de qualidade e o making of completo são exclusivos desses planos.",
      livePreviewOnly:
        "Quem não é membro Gold vê só a prévia de cada parte; as lives completas são exclusivas do plano Gold.",
      photoPartNotFound: "Parte inexistente neste ensaio.",
      photoLocked:
        "As próximas fotos são exclusivas para assinantes. Nada foi aberto; se a pessoa quiser assinar, use start_subscription.",
      photoEnd: "Fim do ensaio: não há próxima foto.",
      photoStart: "Começo do ensaio: não há foto anterior.",
    },
  },

  ingles: {
    tools: {
      search_models: {
        description:
          "Searches Bella Club models by name and returns each one's name and page address. Use it whenever someone mentions a model by name.",
        params: {
          query: "The model's name or part of it, for example 'ana paula'.",
          limit: "How many results to return (1 to 10). Defaults to 8.",
        },
      },
      browse_catalog: {
        description:
          "Lists content published on Bella Club by section (model photo shoots, videos, making of, lives, columns or blog), with pagination, returning titles and page addresses. On the blog and columns it also searches by keyword.",
        params: {
          section:
            "Catalog section: 'models' (photo shoots), 'videos', 'making-of', 'lives', 'columns' or 'blog'.",
          category:
            "Category slug to filter by, from list_categories. Applies to models, videos, making-of, lives and columns.",
          query:
            "Word or phrase to search for, for example 'beach'. Applies to blog and columns; to find a model, use search_models.",
          page: "Page number, starting at 1.",
        },
      },
      list_categories: {
        description:
          "Lists the categories available in a catalog section with each one's name and slug (and item count, when the section reports it). Use it before filtering with browse_catalog.",
        params: {
          section:
            "Catalog section: 'models', 'videos', 'making-of', 'lives' or 'columns'.",
        },
      },
      open_page: {
        description:
          "Opens a Bella Club page in the browser, including the pages of a specific model (photo shoot, videos, making of, lives, profile and wallpapers).",
        params: {
          destination:
            "Where to go: home, models, videos, making-of, lives, columns, blog, ecards, freetour, bellacloud, about, contact, press, subscribe, login, my-account, my-credits, palpitao, area-gold or model (a model's page).",
          model_slug:
            "The model's slug, required when destination is 'model'. Use search_models to find it.",
          model_section:
            "Which model page to open: photo (shoot), video, making-of, live, perfil (profile) or wallpaper. Defaults to photo.",
        },
      },
      set_language: {
        description:
          "Switches the site language between Portuguese and English, keeping the person on the same page.",
        params: { language: "Desired language: 'pt-br' or 'en'." },
      },
      get_subscription_plans: {
        description:
          "Returns the Bella Club subscription plans currently on sale, with tier (Basic, Plus, Gold), length in months, price, discount and what each one unlocks.",
        params: {},
      },
      start_subscription: {
        description:
          "Takes the person to the start of subscription checkout with the chosen plan and coupon already applied. Payment details are still filled in and confirmed by the person.",
        params: {
          plan_type: "Plan tier: 'Basic', 'Plus' or 'Gold'.",
          months: "Plan length in months, for example 1, 3 or 12.",
          coupon: "Discount coupon to apply, if any.",
        },
      },
      get_account_status: {
        description:
          "Reports whether someone is signed in to Bella Club, whether the subscription is active, which plan it is and until when it runs.",
        params: {},
      },
      get_credit_packages: {
        description:
          "Returns the download credit packages on sale, with credit amount and price.",
        params: {},
      },
      get_my_credits: {
        description:
          "Reports how many download credits the account has left and how many downloads were already used today.",
        params: {},
      },
      get_purchase_history: {
        description:
          "Returns the account's purchase history with the date, plan, amount and currency of each order.",
        params: {},
      },
      start_account_update: {
        description:
          "Opens the account details tab of My Account so the person can review and save their changes.",
        params: {},
      },
      start_plan_renewal: {
        description:
          "Opens the renew and upgrade tab of My Account, where the person picks and confirms a new plan.",
        params: {},
      },
      start_referral_code: {
        description:
          "Opens the 'Invite your friend' tab of My Account, where the person generates their sharing code.",
        params: {},
      },
      start_subscription_cancellation: {
        description:
          "Opens the subscription cancellation form. The cancellation itself only happens once the person fills in the reason and confirms.",
        params: {},
      },
      open_help_topic: {
        description:
          "Opens a FAQ topic on the contact page. Opening the topic is what brings up the contact, model suggestion and password reset forms.",
        params: {
          topic: "Title of the FAQ topic to open, exactly as it appears in the list.",
        },
      },
      contact_support: {
        description:
          "Sends a message to Bella Club support. Fills in the contact form for the person to review and send.",
        params: {},
      },
      suggest_model: {
        description:
          "Suggests a model to Bella Club. Fills in the suggestion form for the person to review and send.",
        params: {},
      },
      request_password_reset: {
        description:
          "Requests a password reset e-mail. Fills in the form for the person to review and send.",
        params: {},
      },
      read_article: {
        description:
          "Reads a Bella Club blog post or column and returns its title, date and text, without images. Use it to summarize an article or answer questions about it.",
        params: {
          url: "Address of the post or column, as returned by browse_catalog or get_whats_new. Without an address, reads the article open on the current page.",
        },
      },
      get_model_details: {
        description:
          "Returns a model's details: introduction, photo shoot release date, interview, production credits and what she has on the site (photos, videos, making of, lives), with each page's address.",
        params: {
          model_slug: "The model's slug. Use search_models to find it.",
        },
      },
      get_whats_new: {
        description:
          "Shows what's new on Bella Club: the model of the week and the latest photo shoots, making of, lives, blog posts and columns, with each one's address.",
        params: {},
      },
      get_help_answer: {
        description:
          "Answers a frequently asked question with the Help Center text (renewal, upgrade, bonus, photo and video access, account status, credits, password, cache, suggestions, cancellation or contact) and returns the address of the page that solves it.",
        params: {
          topic: "Title of the Help Center topic, exactly as listed.",
        },
      },
      get_download_status: {
        description:
          "Shows the videos the person requested to download with credits: whether each one is available, expired or still processing, and until when its link is valid. The download itself is done by the person on the downloads page.",
        params: {},
      },
      control_video: {
        description:
          "Controls the video player open on this page: play, pause or restart from the beginning. Returns whether it is playing, the part, the quality and the time.",
        params: {
          action: "'play', 'pause' or 'restart' (back to the start and play).",
        },
      },
      choose_video: {
        description:
          "Chooses the part and the quality of the video open on this page (photo shoot video, making of or live; for lives, only the part). Without arguments, it only lists the available parts and qualities.",
        params: {
          part: "Part number, starting at 1.",
          quality: "Video quality, exactly as listed, for example 'HD 720p'.",
        },
      },
      navigate_photos: {
        description:
          "Moves through the photos of the photo shoot open on this page: next, previous or the start of a part. Returns the part, the section and the photo's position.",
        params: {
          action: "'next', 'previous' or 'part' (go to a part).",
          part: "Part number, starting at 1. Required when action is 'part'.",
        },
      },
      photo_slideshow: {
        description:
          "Turns the automatic photo slideshow of the photo shoot open on this page on or off, with the chosen interval.",
        params: {
          action: "'start' or 'stop'.",
          seconds: "Seconds between photos, from 1 to 10. Without a value, keeps the current interval.",
        },
      },
      subscribe_newsletter: {
        description:
          "Signs an e-mail address up to the Bella Club newsletter. Fills in the form for the person to review and send.",
        params: {},
      },
    },
    messages: {
      notLoggedIn:
        "Nobody is signed in on this browser. Ask the person to sign in at /en/login and try again.",
      noResults: "No results found.",
      error: "Could not complete this right now. Please try again shortly.",
      navigated: "Page opened:",
      languageChanged: "Language switched to",
      unknownSection: "Unknown section.",
      unknownDestination: "Unknown destination.",
      modelNotFound: "Model not found. Use search_models to get the right slug.",
      confirmInPage:
        "Nothing was submitted: the person needs to review and confirm on the page itself.",
      topicNotFound: "Topic not found in the FAQ list.",
      topicOpened: "Topic opened:",
      planSelectionOpen:
        "The plans screen is open with the chosen tier. The person picks the period and confirms payment — nothing has been charged.",
      searchOnlyArticles:
        "Keyword search only applies to blog and columns. To find a model, use search_models.",
      articleNotFound:
        "Post or column not found. Use browse_catalog to get the right address.",
      notAnArticle:
        "That address is not a blog post or a column. Pass an article address, as returned by browse_catalog.",
      textTruncated: "The text was cut here; the rest is on the article's page.",
      autoplayBlocked:
        "The browser did not let the video start on its own. Ask the person to click the player's play button.",
      noPlayer: "The player is still loading. Try again in a moment.",
      videoPartNotFound: "This video has no such part. Call choose_video without arguments to see the parts.",
      videoPartWithoutVideo: "That part has no video.",
      videoQualityNotFound:
        "Quality not available in this part. Call choose_video without arguments to see the qualities.",
      videoQuality4k:
        "Exclusive 4K quality for Plus and Gold plan members. To watch in 4K, upgrade your plan or use download credits.",
      liveWithoutQuality: "Lives have no quality choice.",
      videoPreviewOnly:
        "Non-subscribers only see each part's preview; choosing the quality and the full video are for subscribers only.",
      makingOfPreviewOnly:
        "Anyone without a Plus or Gold plan only sees each part's preview; choosing the quality and the full making of are exclusive to those plans.",
      livePreviewOnly:
        "Anyone who is not a Gold member only sees each part's preview; the full lives are exclusive to the Gold plan.",
      photoPartNotFound: "This photo shoot has no such part.",
      photoLocked:
        "The next photos are for subscribers only. Nothing was opened; if the person wants to subscribe, use start_subscription.",
      photoEnd: "End of the photo shoot: there is no next photo.",
      photoStart: "Start of the photo shoot: there is no previous photo.",
    },
  },
};

export type { WebMcpTranslation };
export default webmcp;
